import type { SupabaseClient } from "@supabase/supabase-js";
import { mapScanRow } from "./scan-list";
import { buildScanReportCsv, reportMethodsMetadataLabel, reportStatusesMetadataLabel, serializeReportCsvRow } from "./report-export";
import { reportLocalMidnightMs, shiftReportCalendarDay, resolveReportDateRange, type ReportScanMethod, type ReportScanLifecycleState } from "./report-scan-filters";
import { isServiceTimeZone } from "../lib/service-week";
import { formatDisplayTime } from "../lib/display-time";
export type ReportKind = "scans" | "incidents";
export interface ReportSearchQuery {
    accountId: string;
    kind: ReportKind;
    fromIso: string;
    toIso: string;
    timeZone: string;
    clientId: string | null;
    siteId: string | null;
    guardId: string | null;
    routeId: string | null;
    checkpointId: string | null;
    statuses: readonly string[];
    severities: readonly string[];
    methods: readonly string[];
    reviewRequiredOnly: boolean;
    text: string;
}
export interface ReportSnapshotRow {
    readonly ordinal: number;
    readonly key: string;
    readonly value: Readonly<Record<string, unknown>>;
}
export interface ReportSnapshot {
    readonly snapshotId: string;
    readonly kind: ReportKind;
    readonly capturedAt: string;
    readonly expiresAt: string;
    readonly effectiveFromIso: string;
    readonly effectiveToIso: string;
    readonly total: number;
    readonly rows: readonly ReportSnapshotRow[];
    readonly query: Readonly<ReportSearchQuery>;
}
export interface CatalogueQuery {
    accountId: string;
    kind: ReportKind;
    family: "clients" | "sites" | "guards" | "routes" | "checkpoints";
    search: string;
    clientId: string | null;
    siteId: string | null;
    routeId: string | null;
    after: {
        id: string;
        label: string;
    } | null;
    limit: number;
}
export interface CataloguePage {
    items: {
        id: string;
        label: string;
    }[];
    nextAfter: {
        id: string;
        label: string;
    } | null;
}
export interface ReportSearchTransport {
    load(query: ReportSearchQuery, signal?: AbortSignal): Promise<ReportSnapshot>;
    validate(snapshot: ReportSnapshot, accountId: string, signal?: AbortSignal): Promise<void>;
    catalogue(query: CatalogueQuery, signal?: AbortSignal): Promise<CataloguePage>;
}
export class ReportSearchError extends Error {
    constructor(readonly code: "expired" | "too_large" | "invalid_query" | "unavailable" = "unavailable") {
        super("Report search is unavailable. Refresh the query or check your access.");
        this.name = "ReportSearchError";
    }
}
const MAX_ROWS = 10000, PAGE_SIZE = 200;
const uuid = (v: unknown): v is string => typeof v === "string" && /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(v);
const instant = (v: unknown): v is string => typeof v === "string" && /T.*(?:Z|[+-]\d{2}:\d{2})$/.test(v) && Number.isFinite(Date.parse(v));
const object = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === "object" && !Array.isArray(v);
const exact = (v: Record<string, unknown>, keys: readonly string[]) => Object.keys(v).length === keys.length && keys.every(k => Object.hasOwn(v, k));
function fail(): never { throw new ReportSearchError(); }
const scanKeys = "scan_id client_event_id checkpoint_name site_name route_name guard_name guard_ref route_assignment_id scan_method lifecycle_state server_received_at device_time gps_accuracy_meters gps_permission validation_notes presence_kind verification_review_required".split(" ");
const incidentKeys = "incident_id incident_status category severity title site_name route_name checkpoint_name reported_by_label reviewed_by_label occurred_at submitted_at reviewed_at locked_at notes_present attachment_count photo_count latest_attachment_at".split(" ");
export function resolveSearchDateRange(kind: ReportKind, fromDay: string, toDay: string, timeZone: string, now: Date): {
    fromIso: string;
    toIso: string;
} | null {
    if (kind === "scans") {
        const r = resolveReportDateRange({ preset: "custom", fromDay, toDay }, timeZone, now);
        return r.kind === "ok" ? { fromIso: r.range.fromIso, toIso: r.range.toIso } : null;
    }
    try {
        if (fromDay > toDay || !Number.isFinite(now.getTime()) || !isServiceTimeZone(timeZone))
            return null;
        const from = reportLocalMidnightMs(fromDay, timeZone), end = reportLocalMidnightMs(shiftReportCalendarDay(toDay, 1), timeZone) - 1;
        if (from > now.getTime())
            return null;
        const to = Math.min(end, now.getTime());
        return { fromIso: new Date(from).toISOString(), toIso: new Date(to).toISOString().replace(/\.999Z$/, ".999999Z") };
    }
    catch {
        return null;
    }
}
export function defaultSearchQuery(accountId: string, kind: ReportKind, timeZone: string, now = new Date()): ReportSearchQuery {
    const r = resolveReportDateRange({ preset: "last_90_days" }, timeZone, now);
    if (r.kind !== "ok")
        throw new ReportSearchError("invalid_query");
    return { accountId, kind, timeZone, fromIso: r.range.fromIso, toIso: r.range.toIso, clientId: null, siteId: null, guardId: null, routeId: null, checkpointId: null, statuses: [], severities: [], methods: [], reviewRequiredOnly: false, text: "" };
}
function validQuery(q: ReportSearchQuery): boolean {
    return object(q) && exact(q, ["accountId", "kind", "fromIso", "toIso", "timeZone", "clientId", "siteId", "guardId", "routeId", "checkpointId", "statuses", "severities", "methods", "reviewRequiredOnly", "text"])
        && uuid(q.accountId) && ["scans", "incidents"].includes(q.kind) && instant(q.fromIso) && instant(q.toIso)
        && Date.parse(q.fromIso) <= Date.parse(q.toIso) && isServiceTimeZone(q.timeZone)
        && [q.clientId, q.siteId, q.guardId, q.routeId, q.checkpointId].every(v => v === null || uuid(v))
        && [q.statuses, q.severities, q.methods].every(a => Array.isArray(a) && a.length <= 8 && a.every(v => typeof v === "string" && v.length <= 40))
        && q.statuses.every(v => (q.kind === "scans" ? ["captured", "queued", "synced", "validated", "flagged", "rejected"] : ["draft", "submitted", "under_review", "locked"]).includes(v))
        && q.severities.every(v => q.kind === "incidents" && ["info", "low", "medium", "high", "critical"].includes(v))
        && q.methods.every(v => q.kind === "scans" && ["QR", "NFC", "MANUAL"].includes(v))
        && (q.kind === "scans" || q.reviewRequiredOnly === false)
        && typeof q.reviewRequiredOnly === "boolean" && typeof q.text === "string" && q.text.length <= 120;
}
type Receipt = Omit<ReportSnapshot, "query" | "rows"> & {
    pageSize: number;
};
const receiptKeys = ["status", "snapshotId", "kind", "capturedAt", "expiresAt", "effectiveFromIso", "effectiveToIso", "total", "pageSize"];
function refused(data: unknown): void {
    if (!object(data))
        fail();
    if (data.status !== "ok" && data.status !== "current")
        throw new ReportSearchError(["expired", "too_large", "invalid_query"].includes(String(data.status)) ? data.status as "expired" | "too_large" | "invalid_query" : "unavailable");
}
function parseReceipt(v: unknown, kind: ReportKind): Receipt {
    refused(v);
    if (!object(v) || !exact(v, receiptKeys) || v.status !== "ok" || !uuid(v.snapshotId) || v.kind !== kind
        || ![v.capturedAt, v.expiresAt, v.effectiveFromIso, v.effectiveToIso].every(instant)
        || !Number.isSafeInteger(v.total) || Number(v.total) < 0 || Number(v.total) > MAX_ROWS || v.pageSize !== PAGE_SIZE)
        fail();
    if (Date.parse(String(v.effectiveFromIso)) > Date.parse(String(v.effectiveToIso)) || Date.parse(String(v.expiresAt)) <= Date.parse(String(v.capturedAt)))
        fail();
    const { status: _, ...r } = v;
    return r as unknown as Receipt;
}
function parseRow(v: unknown, kind: ReportKind, ordinal: number): ReportSnapshotRow {
    if (!object(v) || !exact(v, ["ordinal", "key", "value"]) || v.ordinal !== ordinal || !uuid(v.key) || !object(v.value))
        fail();
    const value = v.value;
    const keys = kind === "scans" ? scanKeys : incidentKeys;
    if (!exact(value, keys) || value[kind === "scans" ? "scan_id" : "incident_id"] !== v.key)
        fail();
    if (Object.values(value).some(x => x !== null && typeof x !== "string" && typeof x !== "number" && typeof x !== "boolean"
        || typeof x === "number" && !Number.isFinite(x) || typeof x === "string" && x.length > 20000))
        fail();
    const dates = kind === "scans" ? ["server_received_at", "device_time"] : ["occurred_at", "submitted_at", "reviewed_at", "locked_at", "latest_attachment_at"];
    if (dates.some(k => value[k] !== null && !instant(value[k])))
        fail();
    const nullableString = (x: unknown) => x === null || typeof x === "string";
    const member = (x: unknown, values: readonly string[]) => typeof x === "string" && values.includes(x);
    if (kind === "scans") {
        const strings = ["client_event_id", "checkpoint_name", "site_name", "route_name", "guard_name", "guard_ref", "validation_notes"];
        if (strings.some(k => !nullableString(value[k]))
            || !member(value.scan_method, ["QR", "NFC", "MANUAL"])
            || !member(value.lifecycle_state, ["captured", "queued", "synced", "validated", "flagged", "rejected"])
            || !instant(value.server_received_at)
            || !(value.route_assignment_id === null || uuid(value.route_assignment_id))
            || !(value.gps_permission === null || member(value.gps_permission, ["granted", "denied", "unavailable", "unknown"]))
            || !(value.presence_kind === null || member(value.presence_kind, ["tag", "manual", "adhoc"]))
            || !(value.gps_accuracy_meters === null || (typeof value.gps_accuracy_meters === "number" && value.gps_accuracy_meters >= 0))
            || !(value.verification_review_required === null || typeof value.verification_review_required === "boolean")) fail();
    } else {
        const strings = ["title", "site_name", "route_name", "checkpoint_name", "reported_by_label", "reviewed_by_label"];
        if (strings.some(k => !nullableString(value[k]))
            || !member(value.incident_status, ["draft", "submitted", "under_review", "locked"])
            || !member(value.category, ["safety", "security", "maintenance", "client_note", "other"])
            || !member(value.severity, ["info", "low", "medium", "high", "critical"])
            || !instant(value.occurred_at) || typeof value.notes_present !== "boolean"
            || ["attachment_count", "photo_count"].some(k => !Number.isSafeInteger(value[k]) || Number(value[k]) < 0)
            || Number(value.photo_count) > Number(value.attachment_count)) fail();
    }
    return Object.freeze({ ordinal, key: v.key.toLowerCase(), value: Object.freeze({ ...value }) });
}
type Rpc = {
    rpc(name: string, args: Record<string, unknown>): PromiseLike<{
        data: unknown;
        error: unknown;
    }> & {
        abortSignal?(signal: AbortSignal): PromiseLike<{
            data: unknown;
            error: unknown;
        }>;
    };
};
export function createManagementReportTransport(client: SupabaseClient): ReportSearchTransport {
    const rpcClient = client as unknown as Rpc;
    async function call(name: string, args: Record<string, unknown>, signal?: AbortSignal): Promise<unknown> {
        if (signal?.aborted)
            fail();
        const controller = new AbortController();
        let timer: ReturnType<typeof setTimeout> | undefined;
        const abort = () => controller.abort();
        signal?.addEventListener("abort", abort, { once: true });
        try {
            const request = rpcClient.rpc(name, args);
            const pending = request.abortSignal ? request.abortSignal(controller.signal) : request;
            const deadline = new Promise<never>((_, reject) => {
                if (controller.signal.aborted) {
                    reject(new ReportSearchError());
                    return;
                }
                timer = setTimeout(() => {
                    controller.abort();
                    reject(new ReportSearchError());
                }, 60000);
                controller.signal.addEventListener("abort", () => reject(new ReportSearchError()), { once: true });
            });
            const response = await Promise.race([Promise.resolve(pending), deadline]);
            if (signal?.aborted || response.error)
                fail();
            return response.data;
        }
        catch {
            throw new ReportSearchError();
        }
        finally {
            if (timer)
                clearTimeout(timer);
            signal?.removeEventListener("abort", abort);
        }
    }
    return {
        async load(query, signal) {
            if (!validQuery(query))
                throw new ReportSearchError("invalid_query");
            const bound = Object.freeze(structuredClone(query));
            Object.freeze(bound.statuses);
            Object.freeze(bound.severities);
            Object.freeze(bound.methods);
            const open = parseReceipt(await call("open_management_report_snapshot", { p_account_id: bound.accountId, p_query: bound }, signal), bound.kind);
            if (Date.parse(open.effectiveFromIso) < Date.parse(bound.fromIso) || Date.parse(open.effectiveToIso) > Date.parse(bound.toIso)
                || Date.parse(open.effectiveToIso) > Date.parse(open.capturedAt) || Date.parse(open.expiresAt) - Date.parse(open.capturedAt) > 15 * 60000)
                fail();
            const rows: ReportSnapshotRow[] = [];
            const keys = new Set<string>();
            for (let offset = 0; offset < open.total;) {
                const data = await call("read_management_report_snapshot", { p_account_id: bound.accountId, p_snapshot_id: open.snapshotId, p_offset: offset }, signal);
                refused(data);
                if (!object(data) || !exact(data, [...receiptKeys.filter(k => k !== "pageSize"), "offset", "rows", "nextOffset"]) || data.offset !== offset || !Array.isArray(data.rows))
                    fail();
                for (const k of ["snapshotId", "kind", "capturedAt", "expiresAt", "effectiveFromIso", "effectiveToIso", "total"] as const)
                    if (data[k] !== open[k])
                        fail();
                const size = Math.min(PAGE_SIZE, open.total - offset);
                if (data.rows.length !== size)
                    fail();
                for (const raw of data.rows) {
                    const row = parseRow(raw, bound.kind, rows.length);
                    if (keys.has(row.key))
                        fail();
                    keys.add(row.key);
                    rows.push(row);
                }
                const next = offset + size;
                if (data.nextOffset !== (next === open.total ? null : next))
                    fail();
                offset = next;
            }
            return Object.freeze({ ...open, query: bound, rows: Object.freeze(rows) });
        },
        async validate(snapshot, accountId, signal) {
            if (accountId !== snapshot.query.accountId || !uuid(snapshot.snapshotId) || snapshot.rows.length !== snapshot.total)
                fail();
            const d = await call("validate_management_report_snapshot", { p_account_id: accountId, p_snapshot_id: snapshot.snapshotId }, signal);
            refused(d);
            if (!object(d) || !exact(d, ["status", "snapshotId", "total"]) || d.status !== "current" || d.snapshotId !== snapshot.snapshotId || d.total !== snapshot.total)
                fail();
        },
        async catalogue(q, signal) {
            const choice = (v: unknown): v is { id: string; label: string } => object(v) && exact(v, ["id", "label"]) && uuid(v.id) && typeof v.label === "string" && v.label.length > 0 && Array.from(v.label).length <= 200;
            if (!object(q) || !uuid(q.accountId) || !["scans", "incidents"].includes(q.kind) || !["clients", "sites", "guards", "routes", "checkpoints"].includes(q.family) || typeof q.search !== "string" || q.search.length > 120 || !Number.isInteger(q.limit) || q.limit < 1 || q.limit > 50 || [q.clientId, q.siteId, q.routeId].some(v => v !== null && !uuid(v)) || !(q.after === null || choice(q.after)))
                throw new ReportSearchError("invalid_query");
            const bound = structuredClone(q);
            const d = await call("search_management_report_catalogue", { p_account_id: bound.accountId, p_kind: bound.kind, p_family: bound.family, p_search: bound.search, p_client_id: bound.clientId, p_site_id: bound.siteId, p_route_id: bound.routeId, p_after: bound.after, p_limit: bound.limit }, signal);
            refused(d);
            if (!object(d) || !exact(d, ["status", "items", "nextAfter"]) || !Array.isArray(d.items) || d.items.length > bound.limit || !d.items.every(choice) || !(d.nextAfter === null || choice(d.nextAfter)) || new Set(d.items.map(v => v.id.toLowerCase())).size !== d.items.length)
                fail();
            if (d.nextAfter !== null && (d.items.length === 0 || d.items.at(-1)?.id !== d.nextAfter.id || d.items.at(-1)?.label !== d.nextAfter.label))
                fail();
            if (bound.after !== null && d.items.some(v => v.id.toLowerCase() === bound.after!.id.toLowerCase()))
                fail();
            return { items: d.items, nextAfter: d.nextAfter } as CataloguePage;
        },
    };
}
export function buildSnapshotCsv(snapshot: ReportSnapshot): string {
    const rows = snapshot.rows;
    if (rows.length !== snapshot.total || new Set(rows.map(r => r.key)).size !== rows.length || new Set(rows.map(r => r.ordinal)).size !== rows.length || rows.some(r => !Number.isInteger(r.ordinal) || r.ordinal < 0 || r.ordinal >= rows.length))
        fail();
    const scopeRows = [serializeReportCsvRow(["Snapshot captured at", snapshot.capturedAt]), serializeReportCsvRow(["Complete selected query rows", String(snapshot.total)]), serializeReportCsvRow(["Client scope", snapshot.query.clientId ? "Selected client" : "All authorized clients"]), serializeReportCsvRow(["Text search", snapshot.query.text ? "Text filter applied" : "None"])];
    if (snapshot.kind === "scans")
        return buildScanReportCsv(rows.map(r => mapScanRow(r.value)), { timeZone: snapshot.query.timeZone, metadata: { windowLabel: "Complete captured result for selected filters", site: snapshot.query.siteId ? "Selected site" : "All sites", guard: snapshot.query.guardId ? "Selected person" : "All people", route: snapshot.query.routeId ? "Selected route" : "All routes", checkpoint: snapshot.query.checkpointId ? "Selected checkpoint" : "All checkpoints", method: reportMethodsMetadataLabel(snapshot.query.methods as ReportScanMethod[]), status: reportStatusesMetadataLabel(snapshot.query.statuses as ReportScanLifecycleState[]), reviewRequired: snapshot.query.reviewRequiredOnly ? "Yes" : "No", dates: `Received by server: ${snapshot.effectiveFromIso} to ${snapshot.effectiveToIso}`, capped: false, exportedAt: new Date(), sort: "Frozen result order; complete selected query" } }) + "\r\n" + scopeRows.join("\r\n");
    const fields = ["title", "site_name", "route_name", "category", "severity", "incident_status", "occurred_at", "submitted_at"] as const;
    const lines = [serializeReportCsvRow(["Title", "Site", "Route", "Category", "Severity", "Status", "Occurred", "Submitted"]), ...rows.map(r => serializeReportCsvRow(fields.map(k => r.value[k] === null || r.value[k] === undefined ? "" : k.endsWith("_at") ? formatDisplayTime(String(r.value[k]), snapshot.query.timeZone) : String(r.value[k]))))];
    lines.push("", serializeReportCsvRow(["# SecOps incident report · metadata"]), serializeReportCsvRow(["Date basis", "Occurred"]), serializeReportCsvRow(["Effective from", snapshot.effectiveFromIso]), serializeReportCsvRow(["Effective to", snapshot.effectiveToIso]), serializeReportCsvRow(["Time zone", snapshot.query.timeZone]), serializeReportCsvRow(["Statuses", snapshot.query.statuses.join(", ") || "All statuses"]), serializeReportCsvRow(["Severities", snapshot.query.severities.join(", ") || "All severities"]), ...scopeRows);
    return lines.join("\r\n");
}
